import * as React from 'react';
import styled, { css } from 'styled-components';
import Arrow from '@/components/FeaturedCatalogCarousel/Arrow';
import { getImageUrl } from '@/utils/urls';
import { getItem } from '@/redux/modules/item';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';
import Slider from 'react-slick/lib/slider';
import { useSelector } from 'react-redux';

type Props = {
    className?: string;
    hideArrows: boolean;
    itemId: number;
};

const ImageSlider = ({ className, hideArrows, itemId }: Props) => {
    const { catalogId, imageVersion, photos, sellerId, title } = useSelector((state) => getItem(state, itemId));

    const [atBegining, setAtBegining] = React.useState(true);
    const [atEnd, setAtEnd] = React.useState(false);

    const makeSlidePhotos = (
        catalogId: number,
        itemId: number,
        photos: number[],
        sellerId: number,
        title: string,
        imageVersion: number
    ) => {
        return photos.map<any>((imageNumber, index) => {
            const alt = index ? `${title} - ${index + 1}` : title;
            const imageUrl = getImageUrl({
                catalogId,
                imageNumber,
                imageVersion,
                itemId,
                sellerId,
                width: 310,
            });

            return (
                <ImageContainer key={index}>
                    <StyledImageWithFallback alt={alt} lazy={false} placeholder={false} src={imageUrl} />
                </ImageContainer>
            );
        });
    };

    const slidePhotos = makeSlidePhotos(catalogId, itemId, photos, sellerId, title, imageVersion);

    const afterChange = (index: number) => {
        setAtBegining(index === 0);
        setAtEnd(index === photos.length - 1);
    };

    const settings = {
        afterChange: afterChange,
        arrows: !hideArrows,
        autoplay: false,
        dots: false,
        infinite: false,
        initialSlide: 0,
        lazyLoad: true,
        nextArrow: <StyledRightArrow className="similarNextArrow" disabled={atEnd} next />,
        prevArrow: <StyledLeftArrow className="similarPrevArrow" disabled={atBegining} prev />,
        slidesToScroll: 1,
        slidesToShow: 1,
    };

    return (
        <StyledCarousel className={className}>
            <Slider {...settings}>
                {photos.length
                    ? slidePhotos
                    : [
                          <div key={0}>
                              <ImageWithFallback alt="placeholder" />
                          </div>,
                      ]}
            </Slider>
        </StyledCarousel>
    );
};

export default React.memo<Props>(ImageSlider);

const StyledCarousel = styled.div`
    &&&& {
        .slick-arrow.slick-disabled {
            display: none !important;
        }
    }
`;

const ImageContainer = styled.div`
    max-height: 100%;
    overflow: hidden;
    position: relative;
    padding-bottom: 100%;
    line-height: 0;
    text-decoration: none;
`;

const StyledImageWithFallback = styled(ImageWithFallback)`
    max-height: 100%;
    max-width: 100%;
    height: auto;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    margin: auto;
    display: block;
`;

interface ArrowProps {
    disabled: boolean;
}

const StyledArrow = styled(Arrow)<ArrowProps>`
    &&& {
        align-items: center;
        background: ${({ theme }) => theme.colors.shadowOverlay50};
        color: ${({ theme }) => theme.colors.grey200};
        cursor: pointer;
        display: flex !important;
        font-size: 30px;
        height: 100%;
        justify-content: center;
        transition: all 0.15s ease-in;
        width: 23px;
        z-index: 1;

        &:hover {
            color: ${({ theme }) => theme.colors.blue100};
            ${(props) =>
                props.disabled &&
                css`
                    color: ${({ theme }) => theme.colors.grey200};
                `}
        }

        &::before {
            content: none;
        }
    }
`;

const StyledRightArrow = styled(StyledArrow)`
    && {
        right: 0;
    }
`;

const StyledLeftArrow = styled(StyledArrow)`
    && {
        left: 0;
    }
`;
